import type { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import { TellentPositionsHttpClient } from './positions.http-client';
import { TellentPositionsActions } from './positions.store';

@Injectable({ providedIn: 'root' })
export class TellentPositionsEffects {
  private readonly tellentPositionsHttpClient = inject(TellentPositionsHttpClient);
  private readonly actions$ = inject(Actions);

  public fetchCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentPositionsActions.fetchCollection),
      switchMap(() =>
        this.tellentPositionsHttpClient.fetchPositions().pipe(
          mergeMap(data => [TellentPositionsActions.fetchCollectionSuccess({ payload: { data } })]),
          catchError((error: HttpErrorResponse) =>
            of(TellentPositionsActions.fetchCollectionFailed({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public createPosition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentPositionsActions.createOnePessimistic),
      switchMap(({ payload }) =>
        this.tellentPositionsHttpClient.createPosition(payload.name).pipe(
          mergeMap(position => [
            TellentPositionsActions.createOnePessimisticSuccess({
              payload: {
                data: position,
                correlationId: payload.correlationId,
              },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              TellentPositionsActions.createOnePessimisticFailed({
                payload: { error, correlationId: payload.correlationId },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public fetchPosition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentPositionsActions.fetchOne),
      mergeMap(({ payload }) =>
        this.tellentPositionsHttpClient.fetchPosition(payload.id).pipe(
          map(response => TellentPositionsActions.fetchOneSuccess({ payload: response })),
          catchError(error =>
            of(TellentPositionsActions.fetchOneFailed({ payload: { id: payload.id, error } })),
          ),
        ),
      ),
    ),
  );

  public updatePosition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentPositionsActions.updateOnePessimistic),
      switchMap(({ payload }) =>
        this.tellentPositionsHttpClient.updatePosition(payload.guid, payload.position).pipe(
          mergeMap(position => [
            TellentPositionsActions.updateOnePessimisticSuccess({
              payload: { data: position },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              TellentPositionsActions.updateOnePessimisticFailed({
                payload: { error, data: { guid: payload.guid } },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public removePosition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentPositionsActions.removeOnePessimistic),
      switchMap(({ payload }) =>
        this.tellentPositionsHttpClient.deletePosition(payload.guid).pipe(
          mergeMap(() => [
            TellentPositionsActions.removeOnePessimisticSuccess({
              payload: { data: { guid: payload.guid } },
            }),
          ]),
          catchError((error: HttpErrorResponse) =>
            of(
              TellentPositionsActions.removeOnePessimisticFailed({
                payload: { error, data: payload.guid },
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
